import {
  SimulationClass,
  SimulationClassInstance,
  SimulationClassInstanceValue,
  SimulationOffer,
  SimulationShop,
} from "@/modules/Simulation/types";

export type SimulationParameters = {
  appId?: string;
  versionId?: string;
  platform?: string;
  segmentIds?: string[];
  gameVersion?: string;
  abTestCohortIds?: string[];
  simulationDate?: string;
  useSimulationDate?: boolean;
};

export type RawSimulationClassInstance = Record<string, string>;
export type RawSimulationClass = RawSimulationClassInstance[];
export type RawSimulationPayload = Record<string, RawSimulationClass>;
export type RawOfferSimulationPayload = {
  offers: SimulationOffer[];
};

export const buildParameters = (simulationParameters: SimulationParameters, gameId: string, versionId?: string) => {
  const DefaultGameVersion = "1000.0.0";
  let params: {
    appId?: string;
    versionId?: string;
    platform?: string;
    segments?: string;
    appVersion?: string;
    abTestId?: string; // actually a list sepparated by commas
    cohortId?: string; // actually a list sepparated by commas
    date_time?: string;
    ignore_date_filter?: boolean;
  } = {
    appId: gameId,
    versionId,
    platform: simulationParameters.platform,
  };

  if (simulationParameters.segmentIds?.length) {
    params.segments = simulationParameters.segmentIds.join(",");
  }

  if (simulationParameters.abTestCohortIds?.length) {
    /**
     * Selecting all odd elements as they're abtestIds and even elements as they're cohortIds.
     */
    const abTestIds = simulationParameters.abTestCohortIds.filter((_, index) => index % 2 === 0);
    const cohortIds = simulationParameters.abTestCohortIds.filter((_, index) => index % 2 !== 0);

    params = { ...params, abTestId: abTestIds.join(","), cohortId: cohortIds.join(",") };
  }

  params.appVersion = simulationParameters.gameVersion || DefaultGameVersion;

  if (simulationParameters.useSimulationDate) {
    params.date_time = simulationParameters.simulationDate.toISOString();
  }

  params.ignore_date_filter = false; // Always filter by date for Simulation Tool

  return params;
};

export const buildClassInstanceValues = (instanceValuesData: RawSimulationClassInstance) => {
  const formattedClassInstanceData: SimulationClassInstanceValue[] = [];
  Object.keys(instanceValuesData).forEach((technicalName) => {
    formattedClassInstanceData.push({ technicalName, value: JSON.stringify(instanceValuesData[technicalName]) });
  });

  return formattedClassInstanceData;
};

export const buildClassInstances = (classTechnicalName: string, instanceData: RawSimulationClass) => {
  const instancesData: SimulationClassInstance[] = [];
  Object.keys(instanceData).forEach((instanceId) => {
    instancesData.push({
      id: `id-${classTechnicalName}-${instanceId}`,
      name: `Instance ${parseInt(instanceId, 10) + 1}`,
      values: buildClassInstanceValues(instanceData[instanceId]),
    });
  });
  return instancesData;
};

export const buildSimulationResult = (data: RawSimulationPayload): SimulationClass[] => {
  const rawClasses: SimulationClass[] = [];
  Object.keys(data).forEach((classTechnicalName: string) => {
    if (!["Voodoo_Config", "Voodoo_Debug"].includes(classTechnicalName)) {
      rawClasses.push({
        technicalName: classTechnicalName,
        attributes: [...new Set(data[classTechnicalName].map((value: any) => Object.keys(value)).flat())],
        instances: buildClassInstances(classTechnicalName, data[classTechnicalName]),
      });
    }
  });

  return rawClasses;
};

export const buildOfferSimulationResult = (data: RawOfferSimulationPayload): SimulationOffer[] => {
  const rawOffers: SimulationOffer[] = [];
  Object.values(data.offers).forEach((offer: any) => {
    rawOffers.push({
      id: offer.id,
      name: offer.name,
      type: offer.type,
      campaign: offer.campaign,
      isEndless: offer.isEndless,
    });
  });
  return rawOffers;
};

export const buildShopSimulationResult = (data: SimulationShop): SimulationShop => data;
